import { useRecoilCallback, useRecoilState } from "recoil";
import { DataState, onboardingDataState } from "recoil/onboardingDataAtom";
import { useOnboardingData } from "./useOnboardingData";
import { useMutation } from "@tanstack/react-query";
import { onboardingApi } from "api/onboardingApi";
import { useNavigate } from "react-router-dom";
import { useRef } from "react";
import { captureException } from "../utils/sentry";
import { onboardingPromotionDataState } from "../recoil/onboardingPromotionDataAtom";
import { useOnboardingPromotionData } from "./useOnboardingPromotionData";

interface UseOnboardingPageProps {
  pageKey: string;
  nextPageRoute?: string;
}

export function useOnboardingPromotionPage({
  pageKey,
  nextPageRoute,
}: UseOnboardingPageProps) {
  const navigate = useNavigate();
  const [onboardingData] = useRecoilState(onboardingPromotionDataState);
  const pageDataHook = useOnboardingPromotionData(pageKey);
  const isSavingRef = useRef(false);

  const handleNext = useRecoilCallback(
    (
      { snapshot } // snapshot을 통해 최신 상태에 접근
    ) =>
      async (dataToSave?: DataState) => {
        // 중복 호출 방지: 저장 중이면 즉시 무시
        if (isSavingRef.current) return;

        let currentPageData: DataState;

        if (dataToSave) {
          // 1. Goal_6처럼 데이터가 직접 전달된 경우, 그 데이터를 사용
          currentPageData = dataToSave;
        } else {
          // 2. 데이터가 전달되지 않은 경우(Goal_2), snapshot에서 최신 상태를 가져옴
          const latestOnboardingData =
            await snapshot.getPromise(onboardingDataState);
          currentPageData = latestOnboardingData[pageKey] ?? {};
        }

        // 저장 시작 플래그 세팅
        isSavingRef.current = true;

        if (nextPageRoute) {
          navigate(nextPageRoute);
        }
      },
    [pageKey, nextPageRoute, navigate] // 이 콜백이 의존하는 값들
  );

  return {
    ...pageDataHook,
    handleNext,
  };
}